import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, RefreshCw, PlayCircle, Database, Trash2 } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { useTts, VOICE_IDS, type VoicePref, getTtsCacheStats, clearTtsCache } from "@/lib/elevenVoice";
import { ElevenLabsKeyTester } from "@/components/admin/ElevenLabsKeyTester";

export const Route = createFileRoute("/admin_/audio-health")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Audio health — Recovery Trauma™" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AudioHealthPage,
});

type CheckStatus = "idle" | "running" | "prefetch-ok" | "play-ok" | "error";

type RouteCheck = {
  key: string;
  label: string;
  path: string;
  sample: string;
  voice: VoicePref;
};

const CHECKS: RouteCheck[] = [
  { key: "meditations", label: "Meditations", path: "/meditations", voice: "female", sample: "Settle in and take a slow, gentle breath." },
  { key: "tapping",     label: "Tapping (EFT)", path: "/tapping",   voice: "female", sample: "Even though this feels heavy, I choose to be gentle with myself." },
  { key: "breathing",   label: "Breathing",     path: "/",   voice: "neutral", sample: "Inhale slowly" },
  { key: "yoga",        label: "Yoga / Somatic", path: "/yoga",     voice: "female", sample: "Notice how your body meets the ground." },
  { key: "whisper",     label: "Whisper voice", path: "/meditations", voice: "whisper", sample: "You are safe here, right now." },
];

type Result = {
  status: CheckStatus;
  message?: string;
  ms?: { prefetch?: number; play?: number };
};

function AudioHealthPage() {
  const { caller, prefetch, cancel } = useTts();
  const [results, setResults] = useState<Record<string, Result>>({});
  const [runningAll, setRunningAll] = useState(false);
  const [cacheStats, setCacheStats] = useState(() => getTtsCacheStats());

  useEffect(() => {
    const iv = setInterval(() => setCacheStats(getTtsCacheStats()), 1000);
    return () => clearInterval(iv);
  }, []);

  const update = (key: string, patch: Partial<Result>) =>
    setResults((r) => ({ ...r, [key]: { ...(r[key] ?? { status: "idle" }), ...patch } }));

  async function runOne(check: RouteCheck) {
    const voiceId = VOICE_IDS[check.voice];
    update(check.key, { status: "running", message: undefined, ms: {} });

    const t0 = performance.now();
    let url: string;
    try {
      url = await prefetch(check.sample, voiceId);
    } catch (err) {
      update(check.key, {
        status: "error",
        message: `Prefetch failed: ${err instanceof Error ? err.message : String(err)}`,
      });
      return;
    }
    const prefetchMs = Math.round(performance.now() - t0);

    if (!url || !url.startsWith("data:audio")) {
      update(check.key, {
        status: "error",
        message: `Prefetch returned invalid audio URL`,
        ms: { prefetch: prefetchMs },
      });
      return;
    }
    update(check.key, { status: "prefetch-ok", ms: { prefetch: prefetchMs } });

    // Verify playback: decode via <audio> and confirm currentTime advances.
    const audio = new Audio(url);
    audio.volume = 0.001; // silent by default — health check, not a demo
    const play0 = performance.now();
    try {
      await audio.play();
    } catch (err) {
      update(check.key, {
        status: "error",
        message: `play() rejected: ${err instanceof Error ? err.message : String(err)} (browser may block autoplay; click "Run" from a user gesture)`,
        ms: { prefetch: prefetchMs },
      });
      return;
    }

    const advanced = await new Promise<boolean>((resolve) => {
      const deadline = Date.now() + 3000;
      const iv = setInterval(() => {
        if (audio.currentTime > 0.05) {
          clearInterval(iv);
          resolve(true);
        } else if (Date.now() > deadline) {
          clearInterval(iv);
          resolve(false);
        }
      }, 100);
    });
    try { audio.pause(); } catch {}

    if (!advanced) {
      update(check.key, {
        status: "error",
        message: "Audio never advanced past 0s",
        ms: { prefetch: prefetchMs, play: Math.round(performance.now() - play0) },
      });
      return;
    }

    update(check.key, {
      status: "play-ok",
      ms: { prefetch: prefetchMs, play: Math.round(performance.now() - play0) },
    });
  }

  async function runAll() {
    setRunningAll(true);
    cancel();
    for (const c of CHECKS) {
      // eslint-disable-next-line no-await-in-loop
      await runOne(c);
    }
    setRunningAll(false);
  }

  const summary = CHECKS.reduce(
    (acc, c) => {
      const s = results[c.key]?.status ?? "idle";
      if (s === "play-ok") acc.ok += 1;
      else if (s === "error") acc.fail += 1;
      else if (s === "running") acc.running += 1;
      return acc;
    },
    { ok: 0, fail: 0, running: 0 },
  );

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <Link to="/admin" className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to admin
          </Link>
          <h1 className="text-2xl font-semibold">Audio health check</h1>
          <p className="text-sm text-muted-foreground">
            Runs a live TTS prefetch and silent playback probe against every audio route
            using the same server function members use. Voice sample volume is near-zero.
          </p>
        </div>
        <button
          onClick={runAll}
          disabled={runningAll || !caller}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-gold)] px-4 py-2 text-sm font-medium text-foreground shadow-[var(--shadow-gold)] disabled:opacity-60"
        >
          {runningAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
          {runningAll ? "Running…" : "Run all checks"}
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-emerald-500">OK: {summary.ok}</span>
        <span className="rounded-full bg-red-500/10 px-3 py-1 text-red-500">Failed: {summary.fail}</span>
        {summary.running > 0 && (
          <span className="rounded-full bg-muted px-3 py-1 text-muted-foreground">Running: {summary.running}</span>
        )}
      </div>

      <ElevenLabsKeyTester />

      <CacheStatsPanel
        stats={cacheStats}
        onClear={() => {
          clearTtsCache();
          setCacheStats(getTtsCacheStats());
        }}
      />



      <ul className="space-y-3">
        {CHECKS.map((check) => {
          const r = results[check.key] ?? { status: "idle" as const };
          return (
            <li
              key={check.key}
              className="rounded-2xl border border-border/60 bg-card/70 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={r.status} />
                    <span className="font-medium">{check.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {check.path} · voice: {check.voice}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    “{check.sample}”
                  </p>
                  {r.message && (
                    <p className="mt-1 text-xs text-red-500">{r.message}</p>
                  )}
                  {(r.ms?.prefetch != null || r.ms?.play != null) && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {r.ms?.prefetch != null && <>prefetch {r.ms.prefetch}ms</>}
                      {r.ms?.play != null && <> · play {r.ms.play}ms</>}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => runOne(check)}
                  disabled={r.status === "running" || runningAll}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-xs text-foreground hover:bg-background disabled:opacity-50"
                >
                  {r.status === "running" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {r.status === "idle" ? "Run" : "Re-run"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === "play-ok") return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  if (status === "prefetch-ok") return <CheckCircle2 className="h-4 w-4 text-amber-400" />;
  if (status === "error") return <XCircle className="h-4 w-4 text-red-500" />;
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  return <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function CacheStatsPanel({
  stats,
  onClear,
}: {
  stats: { entries: number; bytes: number; hits: number; misses: number };
  onClear: () => void;
}) {
  const total = stats.hits + stats.misses;
  const hitRate = total > 0 ? Math.round((stats.hits / total) * 100) : 0;
  return (
    <div className="mb-6 rounded-2xl border border-border/60 bg-card/70 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Database className="h-4 w-4 text-muted-foreground" />
          TTS cache
        </div>
        <button
          onClick={onClear}
          className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1 text-xs text-foreground hover:bg-background"
        >
          <Trash2 className="h-3.5 w-3.5" /> Clear
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <Stat label="Hits" value={stats.hits.toString()} accent="text-emerald-500" />
        <Stat label="Misses" value={stats.misses.toString()} accent="text-amber-400" />
        <Stat label="Hit rate" value={total > 0 ? `${hitRate}%` : "—"} />
        <Stat label="Entries" value={stats.entries.toString()} />
        <Stat label="Size" value={formatBytes(stats.bytes)} />
      </dl>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 text-lg font-semibold tabular-nums ${accent ?? "text-foreground"}`}>{value}</dd>
    </div>
  );
}
